import React from 'react';

// --- Styles ---
export const shakaStyles = `
	/* 1. Target Shaka's DOM-based rendering container */
	.shaka-text-container {
		bottom: 20% !important;
		z-index: 50 !important; /* Ensure sits above overlays */
	}

	/* Target the actual text spans created by Shaka */
	.shaka-text-container span {
		background-color: rgba(0, 0, 0, 1) !important; /* Solid Black */
		color: #FACC15 !important; /* Bright Yellow */
		text-shadow: 0px 0px 4px black !important;
		padding: 6px 12px !important;
		border-radius: 6px !important;
		font-family: sans-serif !important;
		font-weight: 700 !important;
		font-size: 1.1rem !important;
		line-height: 1.4 !important;
	}

	/* 2. Target Native Browser Cues (Essential for Safari/Chrome fallback) */
	video::cue {
		background-color: rgba(0, 0, 0, 1) !important;
		color: #FACC15 !important;
		text-shadow: 0px 0px 4px black !important;
		font-family: sans-serif !important;
		font-weight: 700 !important;
	}

	/* Legacy Webkit selector just in case */
	video::-webkit-media-text-track-display {
		background-color: rgba(0, 0, 0, 1) !important;
		color: #FACC15 !important;
	}

	/* Custom Animations */
	@keyframes jiggle {
		0% { transform: rotate(-1deg); }
		50% { transform: rotate(1deg); }
		100% { transform: rotate(-1deg); }
	}
	.animate-jiggle {
		animation: jiggle 0.2s ease-in-out infinite;
	}

	@keyframes pulse-ring {
		0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7); }
		70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(59, 130, 246, 0); }
		100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
	}
	.animate-pulse-ring {
		animation: pulse-ring 2s infinite;
	}
`;

// --- Constants ---
export const DEFAULT_DATA_URL =
  'https://baws.in/assets/catalog/audio_data.json';
export const GAP_SECONDS = 10;
export const HISTORY_KEY = 'baws_audio_history';
export const QUEUE_KEY = 'baws_audio_queue';
export const FAV_KEY = 'baws_audio_favorites';
export const BOOKMARK_KEY = 'baws_audio_bookmarks';
export const FILLER_MUSIC_DURATION = 180; // 3 minutes for SoundHelix-Song-1.mp3

// --- Data Helpers ---
export const generateId = (item) => {
  if (item.id && item.id.toString().trim().length > 0) return item.id;
  const uniqueKey =
    item.dest || item.stream || item.audio || item.url || item.link;
  if (uniqueKey) return `${item.speaker || 'Sujataa'}_${uniqueKey}`;
  return `${item.title}_${item.date}`;
};

export const processData = (data) => {
  if (!Array.isArray(data)) return [];
  const unique = new Map();

  data.forEach((item, index) => {
    const hasSource = item.stream || item.audio || item.dest || item.url;
    if (!hasSource) return;

    const normalizedItem = {
      ...item,
      stream: item.stream || item.url || item.audio,
      title: item.title || `Track ${index + 1}`,
    };

    let id = generateId(normalizedItem);

    if (unique.has(id)) {
      id = `${id}_${index}`;
    }

    unique.set(id, { ...normalizedItem, id });
  });
  return Array.from(unique.values());
};

export const getHistoryMeta = (history, id) => {
  if (!history || !id) return { progress: 0, lastPlayed: 0 };
  const entry = history[id];
  if (entry === undefined) return { progress: 0, lastPlayed: 0 };
  if (typeof entry === 'number') return { progress: entry, lastPlayed: 0 };
  return {
    progress: entry.progress || 0,
    lastPlayed: entry.lastPlayed || 0,
  };
};

// --- Link Parser Helper ---
export const Linkify = ({ text }) => {
  if (!text || typeof text !== 'string') return null;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);

  return (
    <>
      {parts.map((part, i) => {
        if (part.match(urlRegex)) {
          return (
            <a
              key={i}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:underline break-all"
              onClick={(e) => e.stopPropagation()}
            >
              {part}
            </a>
          );
        }
        return part;
      })}
    </>
  );
};

// --- Audio Helpers ---
export const getAudioContext = () => {
  if (!window.AudioContext && !window.webkitAudioContext) return null;
  if (!window._sharedAudioCtx) {
    window._sharedAudioCtx = new (
      window.AudioContext || window.webkitAudioContext
    )();
  }
  return window._sharedAudioCtx;
};

export const playBlip = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(800, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.05, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
    osc.start();
    osc.stop(ctx.currentTime + 0.1);
  } catch (e) {}
};

// --- Formatting and Parsing ---
export const parseDuration = (str) => {
  if (!str) return 0;
  const parts = str.split(':').map(Number);
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return 0;
};

export const formatDurationString = (seconds) => {
  if (isNaN(seconds)) return '00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const mStr = m < 10 ? '0' + m : m;
  const sStr = s < 10 ? '0' + s : s;
  if (h > 0) return `${h}:${mStr}:${sStr}`;
  return `${mStr}:${sStr}`;
};

export const formatTime12 = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) return '--:--';
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

export const formatPartyCountdown = (ms) => {
  if (ms <= 0) return 'Starting...';
  const seconds = Math.floor(ms / 1000);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
};

// --- Shaka Player ---
let shakaPromise = null;
export const loadShakaPlayer = () => {
  if (shakaPromise) return shakaPromise;
  shakaPromise = new Promise((resolve, reject) => {
    if (window.shaka) {
      resolve(window.shaka);
      return;
    }
    const script = document.createElement('script');
    script.src =
      'https://cdnjs.cloudflare.com/ajax/libs/shaka-player/4.3.5/shaka-player.ui.min.js';
    script.onload = () => resolve(window.shaka);
    script.onerror = reject;
    document.head.appendChild(script);
    const style = document.createElement('link');
    style.rel = 'stylesheet';
    style.href =
      'https://cdnjs.cloudflare.com/ajax/libs/shaka-player/4.3.5/controls.min.css';
    document.head.appendChild(style);
  });
  return shakaPromise;
};

// --- Radio Schedule Generation ---
function cyrb128(str) {
  let h1 = 1779033703,
    h2 = 3144134277,
    h3 = 1013904242,
    h4 = 2773480762;
  for (let i = 0, k; i < str.length; i++) {
    k = str.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  return [
    (h1 ^ h2 ^ h3 ^ h4) >>> 0,
    (h2 ^ h1) >>> 0,
    (h3 ^ h1) >>> 0,
    (h4 ^ h1) >>> 0,
  ];
}

function sfc32(a, b, c, d) {
  return function () {
    a >>>= 0;
    b >>>= 0;
    c >>>= 0;
    d >>>= 0;
    var t = (a + b) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = (c << 21) | (c >>> 11);
    d = (d + 1) | 0;
    t = (t + d) | 0;
    c = (c + t) | 0;
    return (t >>> 0) / 4294967296;
  };
}

function shuffle(array, seedString) {
  const seed = cyrb128(seedString);
  const rng = sfc32(seed[0], seed[1], seed[2], seed[3]);
  let currentIndex = array.length,
    randomIndex;
  const newArray = [...array];
  while (currentIndex !== 0) {
    randomIndex = Math.floor(rng() * currentIndex);
    currentIndex--;
    [newArray[currentIndex], newArray[randomIndex]] = [
      newArray[randomIndex],
      newArray[currentIndex],
    ];
  }
  return newArray;
}

const generateBlock = (items, blockStartMs, startOffset = 0) => {
  const RADIO_BLOCK_DURATION = 6 * 60 * 60;
  const schedule = [];

  if (!items || !Array.isArray(items) || items.length === 0) return schedule;

  let pool = items.filter((item) => {
    if (!item.createdAt) return true;
    const created = new Date(item.createdAt).getTime();
    return blockStartMs - created >= 12 * 3600 * 1000;
  });

  if (pool.length === 0) {
    pool = [...items];
  }

  if (pool.length === 0) return schedule;

  pool.sort((a, b) =>
    (a.id || '').toString().localeCompare((b.id || '').toString())
  );

  const seedString = `BawsRadio_Block_${blockStartMs}`;
  const shuffled = shuffle(pool, seedString);

  let accumulatedTime = 0;
  let poolIndex = 0;

  while (accumulatedTime < RADIO_BLOCK_DURATION) {
    const item = shuffled[poolIndex % shuffled.length];
    const duration = parseDuration(item.duration) + 1;
    const safeDuration = duration > 0 ? duration : 180;

    if (
      accumulatedTime + safeDuration + GAP_SECONDS >
      RADIO_BLOCK_DURATION - 300
    )
      break;

    const nextItem = shuffled[(poolIndex + 1) % shuffled.length];

    schedule.push({
      ...item,
      type: 'content',
      playStartOffset: startOffset + accumulatedTime,
      playEndOffset: startOffset + accumulatedTime + safeDuration,
      uniqueKey: `${item.id}-${blockStartMs}-${accumulatedTime}`,
    });
    accumulatedTime += safeDuration;

    schedule.push({
      id: `countdown-${item.id}-${blockStartMs}-${accumulatedTime}`,
      type: 'countdown',
      title: `Up Next: ${nextItem.title}`,
      thumbnail: nextItem.thumbnail,
      duration: '00:10',
      playStartOffset: startOffset + accumulatedTime,
      playEndOffset: startOffset + accumulatedTime + GAP_SECONDS,
      uniqueKey: `countdown-${item.id}-${blockStartMs}-${accumulatedTime}`,
      nextItemTitle: nextItem.title,
      preloadSrc: nextItem.stream || nextItem.audio,
      preloadSubtitles: nextItem.subtitles,
    });
    accumulatedTime += GAP_SECONDS;
    poolIndex++;
  }

  const remainingTime = RADIO_BLOCK_DURATION - accumulatedTime;
  if (remainingTime > 0) {
    const randomStart = Math.floor(Math.random() * FILLER_MUSIC_DURATION);

    schedule.push({
      id: `station-break-${blockStartMs}`,
      type: 'content',
      title: 'Station Break • Music',
      thumbnail:
        'https://images.unsplash.com/photo-1478737270239-2f02b77ac6d5?auto=format&fit=crop&q=80&w=1000',
      audio: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3',
      duration: formatDurationString(remainingTime),
      playStartOffset: startOffset + accumulatedTime,
      playEndOffset: startOffset + RADIO_BLOCK_DURATION,
      uniqueKey: `station-break-${blockStartMs}`,
      isFiller: true,
      isLoopingFiller: true,
      fillerDuration: FILLER_MUSIC_DURATION,
      fillerStartTime: randomStart,
    });
  }
  return schedule;
};

export const generateRadioSchedule = (items, blockStartMsIn) => {
  const RADIO_BLOCK_DURATION = 6 * 60 * 60;
  const blockStartMs = blockStartMsIn || getBlockStart();

  let schedule = generateBlock(items, blockStartMs, 0);

  const nowMs = Date.now();
  const msIntoBlock = nowMs - blockStartMs;
  const threeHoursMs = 3 * 60 * 60 * 1000;

  if (msIntoBlock > threeHoursMs) {
    const nextBlockStartMs = blockStartMs + RADIO_BLOCK_DURATION * 1000;
    const nextBlockSchedule = generateBlock(
      items,
      nextBlockStartMs,
      RADIO_BLOCK_DURATION
    );
    schedule = [...schedule, ...nextBlockSchedule];
  }

  return { schedule, blockStartMs };
};

export const getBlockStart = () => {
  const now = new Date();
  const start = new Date(now);
  start.setUTCHours(Math.floor(start.getUTCHours() / 6) * 6, 0, 0, 0);
  return start.getTime();
};
